import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  List,
  ListItem,
  CircularProgress,
  Paper,
  alpha,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { format, isToday, isYesterday } from 'date-fns';
import useNotificationStore from '../../../stores/notificationStore';
import useTaskStore from '../../../stores/taskStore';
import { useSocketEvent } from '../../../hooks/useSocketEvent';
import notificationSoundService from '../../../services/notificationSoundService';

// Helper function to get icon based on notification type
const getNotificationIcon = (type) => {
  switch (type) {
    case 'approval':
      return <CheckCircleIcon color="success" />;
    case 'system':
      return <InfoIcon color="info" />;
    case 'deadline':
      return <WarningIcon color="warning" />;
    case 'error':
      return <ErrorIcon color="error" />;
    default:
      return <NotificationsIcon  />;
  }
};

// Helper function to format date
const formatDate = (dateString) => {
  const date = new Date(dateString);
  if (isToday(date)) {
    return `Today, ${format(date, 'h:mm a')}`;
  } else if (isYesterday(date)) {
    return `Yesterday, ${format(date, 'h:mm a')}`;
  } else {
    return format(date, 'MMM d, yyyy');
  }
};

// NotificationsMenu component
const NotificationsMenu = ({ anchorEl, open, onClose }) => {
  const navigate = useNavigate();
  const {
    notifications,
    isLoading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotificationStore();
  
  // Fetch notifications when menu opens
  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open, fetchNotifications]);
  
  // Listen for real-time notifications
  useSocketEvent('notification:new', async (data) => {
    console.log('New notification received in NotificationsMenu:', data);
    // Play notification sound based on notification type
    const notificationType = data?.notification?.type || data?.type || 'default';
    console.log('Playing notification sound for type:', notificationType);

    try {
      await notificationSoundService.playSound(notificationType);
      console.log('Notification sound played successfully from NotificationsMenu');
    } catch (error) {
      console.error('Failed to play notification sound in NotificationsMenu:', error);
    }

    // Fetch updated notifications list
    fetchNotifications();
  });
  
  useSocketEvent('notification:updated', (data) => {
    console.log('Notification updated:', data);
    // Fetch updated notifications list
    fetchNotifications();
  });
  
  useSocketEvent('notification:cleared', (data) => {
    console.log('Notifications cleared:', data);
    // Fetch updated notifications list
    fetchNotifications();
  });
  
  // Handle click on notification
  const handleNotificationClick = async (notification) => {
    // Mark as read
    await markAsRead(notification._id);

    // Check if this is a task notification
    if (notification.actionLink && notification.actionLink.startsWith('/tasks/')) {
      // Extract task ID from the actionLink
      const taskId = notification.actionLink.split('/tasks/')[1];
      if (taskId) {
        // Open task drawer directly
        const { openTaskDrawer } = useTaskStore.getState();
        openTaskDrawer(taskId);
      }
    } else if (notification.actionLink) {
      // For non-task notifications, navigate normally
      navigate(notification.actionLink);
    }

    // Close menu
    onClose();
  };
  
  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };
  
  // Handle delete notification
  const handleDeleteNotification = async (event, notificationId) => {
    event.stopPropagation(); // Prevent triggering parent click
    await deleteNotification(notificationId);
  };
  
  return (
    <Menu
      anchorEl={anchorEl}
      id="notifications-menu"
      open={open}
      onClose={onClose}
      PaperProps={{
        elevation: 3,
        sx: (theme) => ({
          overflow: 'hidden',
          filter: theme.palette.mode === 'dark'
            ? 'drop-shadow(0px 2px 12px rgba(0,0,0,0.5))'
            : 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
          mt: 1.5,
          width: 360,
          maxHeight: 480,
          border: theme.palette.mode === 'dark'
            ? `1px solid ${alpha(theme.palette.background.light, 0.1)}`
            : `1px solid ${alpha(theme.palette.background.light, 0.5)}`,
          // Add subtle outline effect in dark mode for better visibility
          ...(theme.palette.mode === 'dark' && {
            outline: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
            outlineOffset: -1,
          }),
          '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: 'background.paper',
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0,
            // Add border for triangle in dark mode
            ...(theme.palette.mode === 'dark' && {
              borderTop: `1px solid ${alpha(theme.palette.background.light, 0.1)}`,
              borderLeft: `1px solid ${alpha(theme.palette.background.light, 0.1)}`,
            }),
          },
        }),
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      <Box sx={(theme) => ({
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.paper, 0.6)
          : alpha(theme.palette.background.paper, 0.8),
        backdropFilter: 'blur(8px)',
        borderBottom: `1px solid ${theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.light, 0.1)
          : alpha(theme.palette.background.light, 0.3)
        }`,
      })}>
        <Typography
          variant="h6"
          sx={(theme) => ({
            fontWeight: 600,
            color: theme.palette.mode === 'dark'
              ? theme.palette.common.white
              : theme.palette.common.black,
            fontSize: '1rem',
          })}
        >
          Notifications
        </Typography>
        <Button
          startIcon={<MarkEmailReadIcon />}
          onClick={handleMarkAllAsRead}
          disabled={notifications.length === 0 || isLoading}
          size="small"
          color="secondary"
          sx={(theme) => ({
            '&.Mui-disabled': {
              color: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.3)
                : alpha(theme.palette.common.black, 0.3),
            },
          })}
        >
          Mark all as read
        </Button>
      </Box>
      
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} color="secondary" />
        </Box>
      ) : error ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography
            sx={(theme) => ({
              color: theme.palette.mode === 'dark'
                ? theme.palette.error.light
                : theme.palette.error.main,
              fontWeight: 500,
            })}
          >
            {error}
          </Typography>
        </Box>
      ) : notifications.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <NotificationsIcon
            sx={(theme) => ({
              fontSize: '2rem',
              color: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.3)
                : alpha(theme.palette.common.black, 0.2),
              mb: 1,
            })}
          />
          <Typography
            sx={(theme) => ({
              color: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.7)
                : theme.palette.text.secondary,
              fontWeight: 500,
            })}
          >
            No notifications
          </Typography>
        </Box>
      ) : (
        <List sx={{ 
          p: 0, 
          overflowY: 'auto',
          maxHeight: 'calc(480px - 120px)', // Account for header and footer height
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none', // Firefox
          '-ms-overflow-style': 'none', // IE/Edge
        }}>
          {notifications.map((notification) => (
            <ListItem
              key={notification._id}
              button
              onClick={() => handleNotificationClick(notification)}
              alignItems="flex-start"
              sx={(theme) => ({
                borderLeft: notification.isRead ? 'none' : '4px solid',
                borderLeftColor: theme.palette.mode === 'dark'
                  ? theme.palette.secondary.main
                  : theme.palette.primary.main,
                bgcolor: notification.isRead
                  ? 'inherit'
                  : theme.palette.mode === 'dark'
                    ? alpha(theme.palette.secondary.main, 0.1)
                    : alpha(theme.palette.primary.main, 0.05),
                my: 0.5,
                borderRadius: '0 4px 4px 0',
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.secondary.main, 0.15)
                    : alpha(theme.palette.primary.main, 0.08),
                },
                // Add focus styles for keyboard navigation
                '&:focus-visible': {
                  outline: theme.palette.mode === 'dark'
                    ? `2px solid ${alpha(theme.palette.secondary.main, 0.5)}`
                    : `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  outlineOffset: -1,
                },
              })}
              secondaryAction={
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={(e) => handleDeleteNotification(e, notification._id)}
                  size="small"
                  sx={(theme) => ({
                    color: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.common.white, 0.7)
                      : alpha(theme.palette.common.black, 0.6),
                    '&:hover': {
                      color: theme.palette.error.main,
                      backgroundColor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.error.main, 0.15)
                        : alpha(theme.palette.error.main, 0.08),
                    },
                  })}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              }
            >
              <ListItemIcon>
                {getNotificationIcon(notification.type)}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant="subtitle2"
                    sx={(theme) => ({
                      fontWeight: 600,
                      color: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.95)
                        : theme.palette.text.primary,
                      fontSize: '0.875rem',
                    })}
                  >
                    {notification.title}
                  </Typography>
                }
                secondary={
                  <React.Fragment>
                    <Typography
                      sx={(theme) => ({
                        display: 'inline',
                        color: theme.palette.mode === 'dark'
                          ? alpha(theme.palette.common.white, 0.85)
                          : theme.palette.text.primary,
                        fontSize: '0.8125rem',
                        lineHeight: 1.5,
                      })}
                      component="span"
                      variant="body2"
                    >
                      {notification.message}
                    </Typography>
                    <Typography
                      component="div"
                      variant="caption"
                      sx={(theme) => ({
                        mt: 0.5,
                        color: theme.palette.mode === 'dark'
                          ? alpha(theme.palette.common.white, 0.6)
                          : theme.palette.text.secondary,
                        fontWeight: 500,
                        fontSize: '0.75rem',
                      })}
                    >
                      {formatDate(notification.createdAt)}
                    </Typography>
                  </React.Fragment>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
      
      <Box sx={(theme) => ({
        p: 2,
        display: 'flex',
        justifyContent: 'center',
        backgroundColor: theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.paper, 0.6)
          : alpha(theme.palette.background.paper, 0.8),
        backdropFilter: 'blur(8px)',
        borderTop: `1px solid ${theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.light, 0.1)
          : alpha(theme.palette.background.light, 0.3)
        }`,
      })}>
        <Button
          onClick={() => {
            navigate('/notifications');
            onClose();
          }}
          size="small"
          color="secondary"
          variant="outlined"
          sx={(theme) => ({
            borderWidth: theme.palette.mode === 'dark' ? 1.5 : 1,
            fontWeight: 600,
            '&:hover': {
              borderWidth: theme.palette.mode === 'dark' ? 1.5 : 1,
              backgroundColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.secondary.main, 0.15)
                : alpha(theme.palette.secondary.main, 0.08),
            },
          })}
        >
          View all notifications
        </Button>
      </Box>
    </Menu>
  );
};

export default NotificationsMenu;